import { Signal } from "../core/Signal.js";
import { Calc } from "../math/Calc.js";
import { Resize } from "./Resize.js";

class ViewportSize {
    public onChange = Signal.create<[ViewportSize]>();
    public onChangeDefault = Signal.create<[ViewportSize]>();
    public onChangeLarge = Signal.create<[ViewportSize]>();
    public onChangeDynamic = Signal.create<[ViewportSize]>();
    public onChangeSmall = Signal.create<[ViewportSize]>();

    private _vw: number;
    private _vh: number;
    private _lvw: number;
    private _lvh: number;
    private _dvw: number;
    private _dvh: number;
    private _svw: number;
    private _svh: number;

    private _isDirty: boolean = false;

    constructor() {
        const w = window.innerWidth;
        const h = window.innerHeight;
        this._vw = w;
        this._vh = h;
        this._lvw = w;
        this._lvh = h;
        this._dvw = w;
        this._dvh = h;
        this._svw = w;
        this._svh = h;

        if (!document.body) {
            document.addEventListener("DOMContentLoaded", () => {
                if(!document.body) {
                    console.error("ViewportSize: document.body is still not available after DOMContentLoaded");
                    return;
                }
                this.create();
            });
        } else {
            this.create();
        }
    }

    private create(): void {
        this.createElement("100vw", "100vh", (w, h) => {
            this._vw = w;
            this._vh = h;
            this.onChangeDefault.dispatch(this);
            this.setDirty();
        });
        this.createElement("100svw", "100svh", (w, h) => {
            this._svw = w;
            this._svh = h;
            this.onChangeSmall.dispatch(this);
            this.setDirty();
        });
        this.createElement("100lvw", "100lvh", (w, h) => {
            this._lvw = w;
            this._lvh = h;
            this.onChangeLarge.dispatch(this);
            this.setDirty();
        });
        this.createElement("100dvw", "100dvh", (w, h) => {
            this._dvw = w;
            this._dvh = h;
            this.onChangeDynamic.dispatch(this);
            this.setDirty();
        });
    }

    private setDirty(): void {
        if (this._isDirty) {
            return;
        }
        this._isDirty = true;
        queueMicrotask(() => {
            this._isDirty = false;
            this.onChange.dispatch(this);
        });
    }

    private createElement(
        width: string,
        height: string,
        onChange: (width: number, height: number) => void
    ): HTMLElement {
        const element = document.createElement("div");
        element.style.visibility = "hidden";
        element.style.pointerEvents = "none";
        element.style.position = "fixed";
        element.style.contain = "strict";
        element.style.width = width;
        element.style.height = height;
        element.style.left = "100%";
        element.setAttribute("aria-hidden", "true");
        element.setAttribute("inert", "");
        document.body.appendChild(element);

        const resizer = Resize.create(element);
        resizer.add((entries) => {
            const w = entries[0].contentRect.width || window.innerWidth;
            const h = entries[0].contentRect.height || window.innerHeight;
            onChange(w, h);
        });

        const style = window.getComputedStyle(element);
        const w = Calc.parse(style.width, 0) || window.innerWidth;
        const h = Calc.parse(style.height, 0) || window.innerHeight;
        onChange(w, h);

        return element;
    }

    public get vw(): number {
        return this._vw;
    }

    public get vh(): number {
        return this._vh;
    }

    public get lvw(): number {
        return this._lvw;
    }

    public get lvh(): number {
        return this._lvh;
    }

    public get dvw(): number {
        return this._dvw;
    }

    public get dvh(): number {
        return this._dvh;
    }

    public get svw(): number {
        return this._svw;
    }

    public get svh(): number {
        return this._svh;
    }

    public toString(): string {
        return `ViewportSize: vw: ${this.vw}, vh: ${this.vh}, lvw: ${this.lvw}, lvh: ${this.lvh}, dvw: ${this.dvw}, dvh: ${this.dvh}, svw: ${this.svw}, svh: ${this.svh}`;
    }
}

export const viewportSize = new ViewportSize();
